/**
 * Connectors over the server API (api.md "Connectors (MCP tokens)", connector.md "Authentication").
 * The list comes from `GET /api/connectors` (a session on a plan with connectors); its query key is
 * `['connectors']`, which the engine also invalidates when the synced `connectors` table changes.
 *
 * The token and the secret URL exist only in the answer to create / rotate. They are kept in memory here,
 * keyed by connector id, for the editor that shows them once — a reload forgets them and only the hint
 * remains.
 */

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import type { ConnectorAccess, ConnectorsResponse, DigesterError } from '@digester/core';
import { useServices } from '../../app/services.js';
import { qk } from '../../engine-client/keys.js';
import { useAccountState } from '../../account/shared.js';

export const ACCESS_OPTIONS: ReadonlyArray<readonly [ConnectorAccess, string]> = [
  ['read', 'Read only'],
  ['organise', 'Read & organise'],
  ['full', 'Read, organise & settings'],
];

export function accessLabel(access: ConnectorAccess): string {
  return ACCESS_OPTIONS.find(([a]) => a === access)?.[1] ?? access;
}

/** The prototype's capability grid, by access level (connector.md "Access levels"). */
export function capabilities(access: ConnectorAccess): Array<{ label: string; on: boolean }> {
  return [
    { label: 'Read messages', on: true },
    { label: 'Create briefings', on: access !== 'read' },
    { label: 'Move messages', on: access !== 'read' },
    { label: 'Update settings', on: access === 'full' },
  ];
}

export interface ConnectorsState {
  /** The plan includes connectors and an account is signed in. */
  available: boolean;
  /** Local state still loading. */
  ready: boolean;
  query: UseQueryResult<ConnectorsResponse, DigesterError> | null;
}

export function useConnectors(): ConnectorsState {
  const { api } = useServices();
  const account = useAccountState();
  const available = account.signedIn && (account.entitlement?.limits.connectors ?? 0) > 0;
  const query = useQuery<ConnectorsResponse, DigesterError>({
    queryKey: qk.connectors(),
    queryFn: () => api.call<ConnectorsResponse>('connectorsList'),
    enabled: available,
  });
  return { available, ready: account.ready, query: available ? query : null };
}

export function useRefreshConnectors(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: qk.connectors() });
}

// ── Secrets shown once ─────────────────────────────────────────────────────────────────────────

export interface RevealedSecrets {
  token: string;
  secretUrl: string;
}

const revealed = new Map<string, RevealedSecrets>();
const listeners = new Set<() => void>();
let version = 0;

export function revealSecrets(connectorId: string, secrets: RevealedSecrets): void {
  revealed.set(connectorId, secrets);
  version++;
  for (const l of listeners) l();
}

export function forgetSecrets(connectorId: string): void {
  if (!revealed.delete(connectorId)) return;
  version++;
  for (const l of listeners) l();
}

export function useRevealedSecrets(connectorId: string | null): RevealedSecrets | null {
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
    () => version,
  );
  return connectorId ? (revealed.get(connectorId) ?? null) : null;
}

// ── Client snippets ────────────────────────────────────────────────────────────────────────────

export interface Snippet {
  id: string;
  title: string;
  text: string;
}

/** How to add the connection to common clients: header auth where the client supports it, else the URL. */
export function clientSnippets(endpoint: string, secrets: RevealedSecrets): Snippet[] {
  const config = {
    mcpServers: {
      digester: { type: 'http', url: endpoint, headers: { Authorization: `Bearer ${secrets.token}` } },
    },
  };
  return [
    { id: 'claude-code', title: 'Claude Code', text: `claude mcp add --transport http digester ${endpoint} --header "Authorization: Bearer ${secrets.token}"` },
    { id: 'json', title: 'MCP client configuration (JSON)', text: JSON.stringify(config, null, 2) },
    { id: 'url', title: 'Connector dialogs that take only a URL (ChatGPT, Claude)', text: secrets.secretUrl },
  ];
}
